import { useEffect, useRef, useState } from 'react'
import { GripVertical, Plus, Trash2 } from 'lucide-react'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import {
  composeInviteSectionHtml,
  eligibilityAcknowledgements,
  feeAcknowledgements,
  htmlWithoutLastList,
  rentalAcknowledgements,
} from '@/features/auth/utils/inviteAcceptanceContent'

type DraftItem = {
  id: string
  label: string
  required: boolean
}

type InviteAcknowledgementEditorProps = {
  id: string
  value: string
  onChange: (html: string) => void
  kind: 'fee' | 'eligibility' | 'rental'
}

let draftSeq = 0

function nextDraftId() {
  draftSeq += 1
  return `ack-${draftSeq}`
}

function draftFromHtml(html: string, kind: 'fee' | 'eligibility' | 'rental') {
  const parsed =
    kind === 'fee'
      ? feeAcknowledgements(html)
      : kind === 'eligibility'
        ? eligibilityAcknowledgements(html)
        : rentalAcknowledgements(html)
  return {
    body: htmlWithoutLastList(html || ''),
    items: parsed.map((item) => ({
      id: nextDraftId(),
      label: item.label,
      required: item.required,
    })),
  }
}

export function InviteAcknowledgementEditor({
  id,
  value,
  onChange,
  kind,
}: InviteAcknowledgementEditorProps) {
  const lastSent = useRef(value || '')
  const [dragId, setDragId] = useState<string | null>(null)
  const [draft, setDraft] = useState(() => draftFromHtml(value || '', kind))

  useEffect(() => {
    const incoming = value || ''
    if (incoming === lastSent.current) return
    lastSent.current = incoming
    setDraft(draftFromHtml(incoming, kind))
  }, [value, kind])

  function publish(body: string, items: DraftItem[]) {
    const html = composeInviteSectionHtml(body, items)
    lastSent.current = html
    setDraft({ body, items })
    if (html !== (value || '')) onChange(html)
  }

  function reorder(fromId: string, targetId: string) {
    if (!fromId || fromId === targetId) return
    const items = [...draft.items]
    const from = items.findIndex((item) => item.id === fromId)
    const to = items.findIndex((item) => item.id === targetId)
    if (from < 0 || to < 0) return
    const [moved] = items.splice(from, 1)
    items.splice(to, 0, moved)
    publish(draft.body, items)
  }

  return (
    <div className="space-y-5">
      <div>
        <p className="mb-1.5 text-sm font-medium text-slate-700">Body text</p>
        <CKEditor4 id={id} value={draft.body} onChange={(body) => publish(body, draft.items)} />
      </div>

      <div className="border-t border-slate-200 pt-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-800">Acknowledgements</h3>
        <div className="space-y-2">
          {draft.items.map((item, index) => (
            <div
              key={item.id}
              className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-2 py-2 sm:flex-nowrap"
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault()
                reorder(event.dataTransfer.getData('text/plain') || dragId || '', item.id)
                setDragId(null)
              }}
            >
              <button
                type="button"
                draggable
                aria-label={`Reorder acknowledgement ${index + 1}`}
                className="cursor-grab px-1 text-slate-300 active:cursor-grabbing"
                onDragStart={(event) => {
                  event.dataTransfer.setData('text/plain', item.id)
                  event.dataTransfer.effectAllowed = 'move'
                  setDragId(item.id)
                }}
                onDragEnd={() => setDragId(null)}
              >
                <GripVertical className="h-4 w-4" aria-hidden />
              </button>
              <input
                type="text"
                value={item.label}
                aria-label={`Acknowledgement ${index + 1}`}
                placeholder="Acknowledgement shown to the applicant"
                className="min-w-0 w-full flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 sm:w-auto"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.preventDefault()
                }}
                onChange={(event) => {
                  const items = draft.items.map((row) =>
                    row.id === item.id ? { ...row, label: event.target.value } : row,
                  )
                  publish(draft.body, items)
                }}
              />
              <label className="flex shrink-0 items-center gap-2 px-1 text-sm text-slate-600">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-violet-600"
                  checked={item.required}
                  aria-label={`Required acknowledgement ${index + 1}`}
                  onChange={(event) => {
                    const items = draft.items.map((row) =>
                      row.id === item.id ? { ...row, required: event.target.checked } : row,
                    )
                    publish(draft.body, items)
                  }}
                />
                Required
              </label>
              <button
                type="button"
                aria-label={`Remove acknowledgement ${index + 1}`}
                className="rounded-lg p-2 text-red-500 hover:bg-red-50"
                onClick={() =>
                  publish(
                    draft.body,
                    draft.items.filter((row) => row.id !== item.id),
                  )
                }
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-violet-400 px-3 py-2 text-sm font-medium text-violet-700 hover:bg-violet-50"
          onClick={() =>
            publish(draft.body, [
              ...draft.items,
              { id: nextDraftId(), label: '', required: true },
            ])
          }
        >
          <Plus className="h-4 w-4" aria-hidden />
          Add acknowledgement
        </button>
      </div>
    </div>
  )
}
